import { useMemo, useState, type ReactNode } from "react";
import guideMd from "../../../../docs/guides/connect-from-your-laptop.md?raw";
import { Card, Page, PageHeader } from "../calm.tsx";
import { localizeGuide, parseGuide, type GuideNode } from "../guide.ts";
import { cx } from "../ui.tsx";
import { SeatsPanel } from "../components/SeatsPanel.tsx";

export function ConnectPage() {
  const md = useMemo(() => localizeGuide(guideMd, typeof window === "undefined" ? undefined : window.location), []);
  const nodes = useMemo(() => parseGuide(md), [md]);
  const hasOwnH1 = nodes[0]?.type === "h1";
  const title = hasOwnH1 ? <>{inline((nodes[0] as { text: string }).text)}</> : "Connect";
  const body = hasOwnH1 ? nodes.slice(1) : nodes;

  return (
    <Page>
      <PageHeader title={title} meta={<span className="text-ink-3">docs/guides/connect-from-your-laptop.md</span>} />
      <div className="mt-8"><SeatsPanel /></div>
      <Card className="mt-8">
        <div className="md space-y-4 text-[14px] leading-relaxed text-ink-2" data-testid="connect-guide">
          {body.map((n, i) => (
            <GuideBlock key={i} node={n} />
          ))}
        </div>
      </Card>
    </Page>
  );
}

function GuideBlock({ node }: { node: GuideNode }) {
  switch (node.type) {
    case "h1":
      return <h1 className="text-[22px] font-semibold tracking-[-0.02em] text-ink">{inline(node.text)}</h1>;
    case "h2":
      return <h2 className="mt-6 text-[17px] font-semibold tracking-[-0.01em] text-ink">{inline(node.text)}</h2>;
    case "blockquote":
      return <blockquote className="border-l-2 border-rule pl-3 italic text-ink-3">{inline(node.text)}</blockquote>;
    case "ul":
      return (
        <ul className="list-disc space-y-1.5 pl-5">
          {node.items.map((item, j) => (
            <li key={j}>{inline(item)}</li>
          ))}
        </ul>
      );
    case "table":
      return (
        <div className="overflow-x-auto">
          <table>
            <thead>
              <tr>
                {node.head.map((c, j) => (
                  <th key={j}>{inline(c)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {node.rows.map((r, k) => (
                <tr key={k}>
                  {r.map((c, j) => (
                    <td key={j}>{inline(c)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case "code":
      return <CodeBlock lang={node.lang} code={node.code} />;
    default:
      return <p>{inline(node.text)}</p>;
  }
}

/** A fenced code block with a copy button (AC2). Flags the node-init command for the E2E test. */
function CodeBlock({ code, lang }: { code: string; lang: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
    } catch {
      // No clipboard permission (e.g. an insecure or headless context): the button still
      // confirms the click so sighted and keyboard users get the same feedback either way.
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="group relative" data-lang={lang || undefined} data-testid={code.includes("node init") ? "node-init-command" : undefined}>
      <pre className="overflow-x-auto rounded-md border border-rule bg-paper-2 p-3 pr-16 font-mono text-[12.5px] leading-relaxed text-ink">
        <code>{code}</code>
      </pre>
      <button
        type="button"
        onClick={copy}
        className="absolute right-2 top-2 rounded-md border border-rule bg-card px-2 py-1 text-[11px] text-ink-2 opacity-0 transition-opacity hover:text-ink group-hover:opacity-100 focus-visible:opacity-100"
        data-testid="copy-code"
        aria-label="Copy command"
      >
        {copied ? "Copied" : "Copy"}
      </button>
    </div>
  );
}

function inline(text: string): ReactNode[] {
  const out: ReactNode[] = [];
  const re = /(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\([^)]+\))/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text))) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const tok = m[0];
    if (tok.startsWith("**")) {
      out.push(<strong key={m.index}>{tok.slice(2, -2)}</strong>);
    } else if (tok.startsWith("`")) {
      out.push(<code key={m.index}>{tok.slice(1, -1)}</code>);
    } else {
      const [, label, href] = tok.match(/^\[([^\]]+)\]\(([^)]+)\)$/) ?? [, tok, "#"];
      out.push(
        <a key={m.index} href={href} className={cx("underline", "text-ink-2 hover:text-ink")}>
          {label}
        </a>,
      );
    }
    last = m.index + tok.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}
